import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Package, Shield, AlertTriangle, CheckCircle2, Sparkles, Box, FileText } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { useToast } from '../hooks/use-toast';
import { Helmet } from 'react-helmet-async';

const InternationalPerfumeShippingPage = () => {
  const [formData, setFormData] = useState({ name: '', email: '', phone: '', details: '' });
  const { toast } = useToast();
  
  const handleChange = (e) => setFormData({ ...formData, [e.target.name]: e.target.value });
  
  const handleSubmit = (e) => {
    e.preventDefault();
    toast({ 
      title: "Perfume Shipping Inquiry Received!", 
      description: "We'll review your perfume shipping requirements and contact you within 24 hours." 
    });
    setFormData({ name: '', email: '', phone: '', details: '' });
  };

  const features = [
    {
      icon: Shield,
      title: "Carrier Approval Required",
      description: "Perfume shipping requires carrier approval based on alcohol content and dangerous goods classification."
    },
    {
      icon: Package,
      title: "Specialized Packaging",
      description: "Fragile glass bottles require professional packaging with proper cushioning and leak protection."
    },
    {
      icon: FileText,
      title: "Documentation Support",
      description: "We assist with customs declarations and dangerous goods documentation where required."
    },
    {
      icon: AlertTriangle,
      title: "Restrictions Apply",
      description: "Alcohol-based perfumes may have shipping restrictions depending on destination and carrier policies."
    }
  ];

  const eligibilityFactors = [
    'Alcohol percentage in fragrance',
    'Bottle size and quantity',
    'Destination country regulations',
    'Carrier acceptance policies',
    'Dangerous goods classification',
    'Proper packaging requirements'
  ];

  const faqs = [
    {
      question: "Can I ship perfume internationally?",
      answer: "Perfume shipping is possible but subject to carrier approval and destination restrictions. Alcohol-based perfumes are classified as dangerous goods. We review each shipment for compliance with carrier and international regulations."
    },
    {
      question: "What perfumes can be shipped?",
      answer: "Non-alcoholic fragrances typically have fewer restrictions. Alcohol-based perfumes require dangerous goods documentation and may be restricted by certain carriers or to specific destinations. Contact us with product details for eligibility review."
    },
    {
      question: "How should perfume be packaged?",
      answer: "Perfume bottles must be securely cushioned, placed in sealed plastic bags to prevent leakage, and packed in rigid outer boxes. Commercial perfume shipments require compliance with dangerous goods packaging regulations."
    },
    {
      question: "Are there customs duties on perfume?",
      answer: "Yes, perfume imports typically incur customs duties and taxes. Rates vary by destination country and declared value. Accurate customs declarations are required."
    }
  ];

  return (
    <>
      <Helmet>
        <title>International Perfume Shipping from Canada | Shippzy</title>
        <meta name="description" content="Professional international perfume and fragrance shipping from Canada. Specialized packaging, carrier approval, and dangerous goods compliance for alcohol-based perfumes." />
        <link rel="canonical" href="https://www.shippzy.ca/international-perfume-shipping" />
      </Helmet>

      <div className="min-h-screen bg-black">
        {/* Hero Section - Luxury Black & Gold */}
        <section className="relative bg-gradient-to-br from-black via-gray-900 to-amber-950 text-white py-24 overflow-hidden">
          {/* Animated Gold Particles */}
          <div className="absolute inset-0 pointer-events-none overflow-hidden">
            {[...Array(20)].map((_, i) => (
              <motion.div
                key={i}
                className="absolute w-1 h-1 bg-amber-400/40 rounded-full"
                animate={{
                  y: [-20, 1000],
                  x: [Math.random() * 100 - 50, Math.random() * 100 - 50],
                  opacity: [0, 1, 0],
                  scale: [0, 1.5, 0]
                }}
                transition={{
                  duration: 8 + Math.random() * 4,
                  repeat: Infinity,
                  delay: Math.random() * 5
                }}
                style={{
                  left: `${Math.random() * 100}%`,
                  top: -20
                }}
              />
            ))}
          </div>

          <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid md:grid-cols-2 gap-12 items-center">
              <motion.div
                initial={{ opacity: 0, x: -30 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8 }}
              >
                <motion.div
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.6, delay: 0.2 }}
                  className="inline-flex items-center gap-2 mb-6 px-4 py-2 bg-gradient-to-r from-amber-500/20 to-yellow-500/20 border border-amber-500/30 rounded-full"
                >
                  <Sparkles className="w-4 h-4 text-amber-400" />
                  <span className="text-amber-300 text-sm font-semibold">Luxury Fragrance Shipping</span>
                </motion.div>
                
                <h1 className="text-5xl md:text-6xl font-bold mb-6 leading-tight">
                  International
                  <span className="block bg-gradient-to-r from-amber-300 via-yellow-300 to-amber-400 bg-clip-text text-transparent">
                    Perfume Shipping
                  </span>
                </h1>
                
                <p className="text-xl text-gray-300 mb-8">
                  Professional international shipping for perfumes and fragrances from Canada. Specialized handling, carrier approval, and compliance with dangerous goods regulations.
                </p>

                <div className="bg-amber-900/30 border-l-4 border-amber-500 p-4 rounded mb-8">
                  <div className="flex items-start gap-3">
                    <AlertTriangle className="w-5 h-5 text-amber-400 flex-shrink-0 mt-1" />
                    <p className="text-sm text-amber-100">
                      Perfume shipping requires eligibility review. Alcohol-based perfumes are classified as dangerous goods and subject to carrier approval and destination restrictions.
                    </p>
                  </div>
                </div>

                <div className="flex flex-col sm:flex-row gap-4">
                  <Button 
                    className="bg-gradient-to-r from-amber-600 to-yellow-600 hover:from-amber-700 hover:to-yellow-700 text-black font-bold px-8 py-6 text-lg shadow-xl"
                    onClick={() => document.getElementById('eligibility-form').scrollIntoView({ behavior: 'smooth' })}
                  >
                    Request Eligibility Review
                  </Button>
                  <Button 
                    variant="outline"
                    className="border-2 border-amber-500 text-amber-300 hover:bg-amber-500/10 px-8 py-6 text-lg"
                    onClick={() => window.location.href = '/contact'}
                  >
                    Contact Us
                  </Button>
                </div>
              </motion.div>

              {/* Floating Perfume Bottle Animation */}
              <motion.div
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.8, delay: 0.3 }}
                className="hidden md:block relative"
              >
                <div className="relative">
                  {/* Glass reflection effect */}
                  <div className="absolute inset-0 bg-gradient-to-br from-amber-400/20 to-transparent rounded-full blur-3xl"></div>
                  
                  {/* Perfume bottle container */}
                  <motion.div
                    animate={{ 
                      y: [0, -20, 0],
                      rotateY: [0, 10, 0, -10, 0]
                    }}
                    transition={{ 
                      duration: 6, 
                      repeat: Infinity,
                      ease: "easeInOut"
                    }}
                    className="relative"
                    style={{ transformStyle: 'preserve-3d' }}
                  >
                    <div className="w-64 h-80 mx-auto bg-gradient-to-br from-amber-500 via-yellow-500 to-amber-600 rounded-3xl shadow-2xl flex items-center justify-center relative overflow-hidden">
                      {/* Glass shimmer effect */}
                      <motion.div
                        animate={{ x: [-200, 400] }}
                        transition={{ duration: 3, repeat: Infinity, ease: "linear" }}
                        className="absolute inset-0 w-20 bg-gradient-to-r from-transparent via-white/30 to-transparent skew-x-12"
                      />
                      
                      {/* Bottle icon */}
                      <Sparkles className="w-32 h-32 text-black/80" />
                      
                      {/* Decorative elements */}
                      <div className="absolute top-4 right-4 w-16 h-16 bg-yellow-300/30 rounded-full blur-xl"></div>
                      <div className="absolute bottom-4 left-4 w-20 h-20 bg-amber-300/30 rounded-full blur-xl"></div>
                    </div>
                  </motion.div>

                  {/* Floating fragrance mist particles */}
                  {[...Array(8)].map((_, i) => (
                    <motion.div
                      key={i}
                      className="absolute w-2 h-2 bg-amber-300/60 rounded-full blur-sm"
                      animate={{
                        y: [100, -100],
                        x: [0, Math.random() * 40 - 20],
                        opacity: [0, 0.8, 0],
                        scale: [0.5, 1.5, 0.5]
                      }}
                      transition={{
                        duration: 4 + Math.random() * 2,
                        repeat: Infinity,
                        delay: i * 0.5
                      }}
                      style={{
                        left: `${40 + Math.random() * 20}%`,
                        bottom: 0
                      }}
                    />
                  ))}
                </div>
              </motion.div>
            </div>
          </div>
        </section>

        {/* Features Section */}
        <section className="py-20 bg-gradient-to-b from-gray-900 to-black">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-16"
            >
              <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
                Specialized Perfume Shipping Service
              </h2>
              <p className="text-xl text-gray-400 max-w-3xl mx-auto">
                Professional handling of fragrance shipments with compliance expertise
              </p>
            </motion.div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
              {features.map((feature, index) => {
                const Icon = feature.icon;
                return (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: index * 0.1 }}
                  >
                    <Card className="h-full bg-gray-800 border-amber-500/20 hover:border-amber-500/40 transition-all duration-300">
                      <CardHeader>
                        <div className="w-16 h-16 bg-gradient-to-br from-amber-500 to-yellow-600 rounded-2xl flex items-center justify-center mb-4 shadow-lg">
                          <Icon className="w-8 h-8 text-black" />
                        </div>
                        <CardTitle className="text-xl text-white">{feature.title}</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <p className="text-gray-400">{feature.description}</p>
                      </CardContent>
                    </Card>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </section>

        {/* Eligibility Section */}
        <section className="py-20 bg-black">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid md:grid-cols-2 gap-12">
              <motion.div
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
              >
                <h2 className="text-3xl font-bold text-white mb-6">Perfume Shipping Eligibility</h2>
                <p className="text-gray-400 mb-6">
                  Perfume shipping eligibility depends on multiple factors. Each shipment is reviewed individually to ensure compliance with carrier policies and international regulations.
                </p>
                <div className="bg-gray-900 border border-amber-500/30 rounded-lg p-6">
                  <h3 className="font-bold text-amber-400 mb-4">Key Eligibility Factors:</h3>
                  <ul className="space-y-3">
                    {eligibilityFactors.map((factor, index) => (
                      <li key={index} className="flex items-start gap-3">
                        <CheckCircle2 className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
                        <span className="text-gray-300">{factor}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, x: 30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
              >
                <div className="bg-gradient-to-br from-amber-900/40 to-gray-900 border border-amber-500/30 rounded-lg p-8">
                  <h3 className="text-2xl font-bold text-white mb-4">Important Information</h3>
                  <div className="space-y-4 text-gray-300">
                    <p>
                      <strong className="text-amber-400">Dangerous Goods Classification:</strong> Alcohol-based perfumes are classified as dangerous goods due to flammable alcohol content.
                    </p>
                    <p>
                      <strong className="text-amber-400">Carrier Restrictions:</strong> Not all carriers accept perfume shipments. Acceptance depends on alcohol percentage, quantity, and destination.
                    </p>
                    <p>
                      <strong className="text-amber-400">Destination Regulations:</strong> Some countries restrict or prohibit perfume imports or require special permits.
                    </p>
                    <p>
                      <strong className="text-amber-400">Documentation Required:</strong> Dangerous goods declarations and proper customs documentation are mandatory for compliant shipments.
                    </p>
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
        </section>

        {/* FAQs */}
        <section className="py-20 bg-gradient-to-b from-gray-900 to-black">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-12"
            >
              <h2 className="text-4xl font-bold text-white mb-4">Frequently Asked Questions</h2>
            </motion.div>

            <div className="space-y-6">
              {faqs.map((faq, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                >
                  <Card className="bg-gray-800 border-amber-500/20">
                    <CardHeader>
                      <CardTitle className="text-lg text-amber-400">{faq.question}</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-gray-300">{faq.answer}</p>
                    </CardContent>
                  </Card>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Eligibility Form */}
        <section id="eligibility-form" className="py-20 bg-gradient-to-br from-amber-900 via-gray-900 to-black">
          <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
            <Card className="shadow-2xl bg-gray-800 border-amber-500/30">
              <CardHeader>
                <CardTitle className="text-3xl text-center text-white">Request Perfume Shipping Review</CardTitle>
                <p className="text-center text-gray-400 mt-2">
                  Provide your perfume details for eligibility review
                </p>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmit} className="space-y-6">
                  <Input
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    placeholder="Your Name"
                    className="h-12 bg-gray-900 border-amber-500/30 text-white"
                  />
                  <Input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    placeholder="Email"
                    className="h-12 bg-gray-900 border-amber-500/30 text-white"
                  />
                  <Input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    required
                    placeholder="Phone"
                    className="h-12 bg-gray-900 border-amber-500/30 text-white"
                  />
                  <textarea
                    name="details"
                    value={formData.details}
                    onChange={handleChange}
                    required
                    placeholder="Perfume details (brand, alcohol content, bottle size, quantity, destination)"
                    className="w-full h-32 px-4 py-3 bg-gray-900 border border-amber-500/30 rounded-md text-white placeholder-gray-500"
                  />
                  <Button
                    type="submit"
                    className="w-full bg-gradient-to-r from-amber-600 to-yellow-600 hover:from-amber-700 hover:to-yellow-700 text-black font-bold h-14 text-lg"
                  >
                    Submit for Review
                  </Button>
                </form>
              </CardContent>
            </Card>
          </div>
        </section>
      </div>
    </>
  );
};

export default InternationalPerfumeShippingPage;
